import { shuffle } from '../utils'

export type Difficulty = 'easy' | 'medium' | 'hard'
export type GameMode   = 'emoji' | 'shapes'

export type ShapeType = 'circle' | 'triangle' | 'square' | 'pentagon' | 'hexagon' | 'star'

export interface MissingItem {
  id:     string
  mode:   GameMode
  emoji?: string
  shape?: ShapeType
  color?: string
}

// ── Config ────────────────────────────────────────────────────────────────────

export const DIFFICULTY_CONFIG: Record<Difficulty, {
  panelSize:    number   // items per panel
  differences:  number   // total unique items (to find)
  bidirectional: boolean
  columns:      number   // forced column count → guarantees ≥4 rows
}> = {
  easy:   { panelSize: 10, differences: 3, bidirectional: false, columns: 3 },
  medium: { panelSize: 15, differences: 5, bidirectional: true,  columns: 4 },
  hard:   { panelSize: 20, differences: 7, bidirectional: true,  columns: 5 },
}

// ── Emoji pool (~70 distinct, high-contrast, recognizable) ────────────────────

export const EMOJI_POOL: string[] = [
  // Animals
  '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯',
  '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🦆', '🦅',
  '🦋', '🐌', '🐝', '🐢', '🐠', '🐬', '🐳', '🦭', '🦀', '🐙',
  // Food & fruit
  '🍎', '🍊', '🍋', '🍇', '🍓', '🍒', '🍑', '🥝', '🍍', '🥭',
  '🍕', '🍔', '🌮', '🍦', '🎂', '🍩', '🍪', '🧁', '🍫', '🥐',
  // Objects
  '⚽', '🎸', '🎺', '🎻', '🥁', '🎨', '📚', '✏️', '🔑', '💎',
  '🌂', '👒', '🎩', '👗', '👠', '🧲', '🔭', '🎁', '🎈', '🏆',
]

// ── Shape colors (10 accessible, high-contrast) ───────────────────────────────

export const SHAPE_COLORS: string[] = [
  '#E53935', // red
  '#1E88E5', // blue
  '#43A047', // green
  '#FB8C00', // orange
  '#8E24AA', // purple
  '#00ACC1', // cyan
  '#F4511E', // deep orange
  '#3949AB', // indigo
  '#00897B', // teal
  '#E91E63', // pink
]

export const SHAPE_TYPES: ShapeType[] = [
  'circle', 'triangle', 'square', 'pentagon', 'hexagon', 'star',
]

// ── Item generation ───────────────────────────────────────────────────────────

function makeEmojiItem(emoji: string): MissingItem {
  return { id: `emoji-${emoji}`, mode: 'emoji', emoji }
}

function makeShapeItem(shape: ShapeType, color: string): MissingItem {
  return { id: `shape-${shape}-${color}`, mode: 'shapes', shape, color }
}

function buildPool(mode: GameMode, count: number): MissingItem[] {
  if (mode === 'emoji') {
    return shuffle(EMOJI_POOL).slice(0, count).map(makeEmojiItem)
  }
  // shapes: all combos = 6 × 10 = 60, more than enough
  const combos: MissingItem[] = []
  for (const shape of SHAPE_TYPES) {
    for (const color of SHAPE_COLORS) {
      combos.push(makeShapeItem(shape, color))
    }
  }
  return shuffle(combos).slice(0, count)
}

export interface PanelSetup {
  leftSet:    MissingItem[]
  rightSet:   MissingItem[]
  correctIds: string[]   // IDs of items unique to their panel (player must select all)
}

export function buildPanels(mode: GameMode, difficulty: Difficulty): PanelSetup {
  const { panelSize, differences, bidirectional } = DIFFICULTY_CONFIG[difficulty]

  if (!bidirectional) {
    // Easy: one-directional. Left has panelSize items; Right = Left minus `differences` items.
    // Player finds the `differences` items in the Left panel not present in Right.
    const allItems  = buildPool(mode, panelSize)
    const shuffled  = shuffle(allItems)
    const leftOnly  = shuffled.slice(0, differences)    // unique to left
    const shared    = shuffled.slice(differences)       // in both
    const leftSet   = shuffle([...leftOnly, ...shared])
    const rightSet  = shuffle([...shared])
    return {
      leftSet,
      rightSet,
      correctIds: leftOnly.map(i => i.id),
    }
  }

  // Medium / Hard: bidirectional.
  // Split differences roughly evenly between left-only and right-only.
  const leftOnlyCount  = Math.floor(differences / 2)
  const rightOnlyCount = differences - leftOnlyCount
  const sharedCount    = panelSize - leftOnlyCount   // left panel total = panelSize

  const totalNeeded = leftOnlyCount + rightOnlyCount + sharedCount
  const pool = buildPool(mode, totalNeeded)

  const leftOnly  = pool.slice(0, leftOnlyCount)
  const rightOnly = pool.slice(leftOnlyCount, leftOnlyCount + rightOnlyCount)
  const shared    = pool.slice(leftOnlyCount + rightOnlyCount)

  const leftSet  = shuffle([...leftOnly, ...shared])
  const rightSet = shuffle([...rightOnly, ...shared])

  return {
    leftSet,
    rightSet,
    correctIds: [...leftOnly, ...rightOnly].map(i => i.id),
  }
}
